/**
 * O campo "Localização" do cliente, transformado em destino de navegação.
 *
 * O campo é texto livre, e na prática chega de três jeitos:
 *   * link curto do Google ou da Apple (maps.app.goo.gl/…, maps.apple/r/…) —
 *     quase todos. Não carrega coordenada: só o servidor, seguindo o
 *     redirecionamento, descobre aonde ele aponta;
 *   * link longo, com a coordenada no meio (`?q=-22.1,-42.3`, `!3d…!4d…`);
 *   * endereço escrito ("Estr. Volta Grande, km 25 - Leopoldina/MG").
 *
 * O Google Maps abre qualquer um dos três. O Waze só navega bem com
 * coordenada — por isso o trabalho de achá-la.
 */

const NUM = '(-?\\d{1,3}\\.\\d+)';
const VIRGULA = '(?:,|%2C)\\s*';

// Em ordem de confiança: o pino do Google vem antes do centro da vista (`@`),
// que é só para onde a câmera olhava e pode estar a centenas de metros.
const PADROES = [
  new RegExp(`!3d${NUM}!4d${NUM}`),
  new RegExp(`[?&](?:q|ll|query|destination|daddr|sll)=${NUM}${VIRGULA}${NUM}`, 'i'),
  new RegExp(`@${NUM},${NUM}`),
];

export const ehLink = (texto) => /^https?:\/\//i.test(String(texto || '').trim());

/** `{ lat, lng }` escrito no próprio texto, ou `null`. */
export const coordenadasDoLink = (texto) => {
  const t = String(texto || '');
  for (const padrao of PADROES) {
    const m = t.match(padrao);
    if (!m) continue;
    const lat = Number(m[1]);
    const lng = Number(m[2]);
    if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
  }
  return null;
};

const HOSTS_CURTOS = ['maps.app.goo.gl', 'goo.gl', 'maps.apple'];

/** Link curto: a coordenada só aparece seguindo o redirecionamento. */
export const precisaResolver = (texto) => {
  if (!ehLink(texto) || coordenadasDoLink(texto)) return false;
  try {
    return HOSTS_CURTOS.includes(new URL(String(texto).trim()).hostname);
  } catch {
    return false;
  }
};

/** Para o botão do mapa: link vai como está; endereço vira busca. */
export const linkDoMapa = (texto) => {
  const t = String(texto || '').trim();
  if (!t) return null;
  return ehLink(t) ? t : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(t)}`;
};

/**
 * Para o botão do Waze. Com coordenada, navega direto; com endereço escrito,
 * deixa o Waze buscar. Link sem coordenada conhecida não vira busca: a URL
 * do Google como texto de busca no Waze não leva a lugar nenhum.
 */
export const linkDoWaze = (texto, coordenadas = null) => {
  const c = coordenadas || coordenadasDoLink(texto);
  if (c) return `https://waze.com/ul?ll=${c.lat},${c.lng}&navigate=yes`;
  const t = String(texto || '').trim();
  if (t && !ehLink(t)) return `https://waze.com/ul?q=${encodeURIComponent(t)}&navigate=yes`;
  return null;
};
